import { useCallback, useMemo, useState } from 'react';
import moment from 'moment';

import { NewsType } from '../../../api/types/apiGetReport';

import { MONITORING_LEAD_DAYS, MONITORING_MAX_DAYS, SWEEP_MIN_HOURS } from './watchdogCopy';
import { latestPublishedAt } from './watchdogUtils';

/**
 * The Watchdog run itself has no dedicated endpoint yet, so the monitoring
 * window a traveler picks is persisted per report in the browser and the
 * remaining signals (last sweep, next sweep, first-sweep-pending) are derived
 * from the findings payload.
 *
 * TODO(api): replace `readRecord` / `writeRecord` with the Watchdog run
 * endpoints once they exist, and take `lastSweepAt` / `nextSweepAt` / `status`
 * straight from the server instead of deriving them here.
 */

const STORAGE_PREFIX = 'se.watchdog.v1.';

export type WatchdogRecord = {
	start: string;
	end: string;
	startedAt: string;
	lastVisit?: string;
};

export type WatchdogState = 'no-dates' | 'not-started' | 'pending' | 'empty' | 'feed' | 'ended';

function storageKey(reportId: string): string {
	return `${STORAGE_PREFIX}${reportId}`;
}

function readRecord(reportId: string): WatchdogRecord | null {
	if (!reportId || typeof window === 'undefined') {
		return null;
	}
	try {
		const stored = window.localStorage.getItem(storageKey(reportId));
		return stored ? (JSON.parse(stored) as WatchdogRecord) : null;
	} catch {
		return null;
	}
}

function writeRecord(reportId: string, record: WatchdogRecord | null): void {
	if (!reportId || typeof window === 'undefined') {
		return;
	}
	try {
		if (record) {
			window.localStorage.setItem(storageKey(reportId), JSON.stringify(record));
		} else {
			window.localStorage.removeItem(storageKey(reportId));
		}
	} catch {
		// Storage unavailable (private mode / quota) — the UI still works, the
		// window just isn't remembered between visits.
	}
}

/** departure − 3 days → trip end, capped at 10 days and never in the past. */
export function recommendedWindow(
	travelDateFrom: string | null,
	travelDateTo: string | null,
): { start: Date; end: Date } | null {
	if (!travelDateFrom || !travelDateTo) {
		return null;
	}
	const departure = moment(travelDateFrom).startOf('day');
	const tripEnd = moment(travelDateTo).startOf('day');
	if (!departure.isValid() || !tripEnd.isValid()) {
		return null;
	}

	const today = moment().startOf('day');
	const start = moment.max(today, departure.clone().subtract(MONITORING_LEAD_DAYS, 'days'));
	const cap = start.clone().add(MONITORING_MAX_DAYS - 1, 'days');
	const end = moment.min(moment.max(tripEnd, start), cap);

	return { start: start.toDate(), end: end.toDate() };
}

export function maxWindowEnd(start: Date | null): Date | undefined {
	if (!start) {
		return undefined;
	}
	return moment(start)
		.add(MONITORING_MAX_DAYS - 1, 'days')
		.toDate();
}

type UseWatchdogArgs = {
	reportId: string;
	travelDateFrom: string | null;
	travelDateTo: string | null;
	newsItems: NewsType[];
};

export type Watchdog = {
	state: WatchdogState;
	record: WatchdogRecord | null;
	monitoringStart: Date | null;
	monitoringEnd: Date | null;
	lastSweepAt: Date | null;
	nextSweepInHours: number;
	/** Findings published after the traveler's previous visit are badged "New". */
	latestBatchCutoff: number | null;
	start: (_window: { start: Date; end: Date }) => void;
	markVisited: () => void;
};

/** Below this age we assume the first sweep is still running (State D). */
const FIRST_SWEEP_GRACE_MINUTES = 60;

export default function useWatchdog(args: UseWatchdogArgs): Watchdog {
	const { reportId, travelDateFrom, travelDateTo, newsItems } = args;

	const [record, setRecord] = useState<WatchdogRecord | null>(() => readRecord(reportId));

	const hasDates = !!travelDateFrom && !!travelDateTo;
	const items = useMemo(() => newsItems || [], [newsItems]);
	const lastSweepAt = useMemo(() => latestPublishedAt(items), [items]);

	// A report opened on another device still has findings but no local record;
	// treat those as an active Watchdog spanning the travel window.
	const effectiveRecord = useMemo<WatchdogRecord | null>(() => {
		if (record) {
			return record;
		}
		if (items.length > 0 && hasDates) {
			return {
				start: moment(travelDateFrom as string).format(),
				end: moment(travelDateTo as string).format(),
				startedAt: moment(lastSweepAt || new Date()).format(),
			};
		}
		return null;
	}, [record, items.length, hasDates, travelDateFrom, travelDateTo, lastSweepAt]);

	const monitoringStart = effectiveRecord ? moment(effectiveRecord.start).toDate() : null;
	const monitoringEnd = effectiveRecord ? moment(effectiveRecord.end).endOf('day').toDate() : null;

	const state: WatchdogState = useMemo(() => {
		if (!hasDates) {
			return 'no-dates';
		}
		if (!effectiveRecord) {
			return 'not-started';
		}
		if (monitoringEnd && monitoringEnd.getTime() < Date.now() && items.length === 0) {
			return 'ended';
		}
		if (items.length > 0) {
			return 'feed';
		}
		const startedAt = moment(effectiveRecord.startedAt);
		if (moment().diff(startedAt, 'minutes') < FIRST_SWEEP_GRACE_MINUTES) {
			return 'pending';
		}
		return 'empty';
	}, [hasDates, effectiveRecord, monitoringEnd, items.length]);

	const latestBatchCutoff = useMemo(() => {
		if (effectiveRecord?.lastVisit) {
			const lastVisit = moment(effectiveRecord.lastVisit);
			if (lastVisit.isValid()) {
				return lastVisit.valueOf();
			}
		}
		if (!lastSweepAt) {
			return null;
		}
		// No previous visit on record: treat the most recent sweep as "the new batch".
		return moment(lastSweepAt).subtract(2, 'hours').valueOf();
	}, [effectiveRecord, lastSweepAt]);

	const nextSweepInHours = useMemo(() => {
		if (!lastSweepAt) {
			return SWEEP_MIN_HOURS;
		}
		const elapsed = moment().diff(moment(lastSweepAt), 'hours');
		const remaining = SWEEP_MIN_HOURS - elapsed;
		return remaining > 0 ? remaining : SWEEP_MIN_HOURS;
	}, [lastSweepAt]);

	const start = useCallback(
		(nextWindow: { start: Date; end: Date }) => {
			const next: WatchdogRecord = {
				start: moment(nextWindow.start).startOf('day').format(),
				end: moment(nextWindow.end).startOf('day').format(),
				startedAt: moment().format(),
				lastVisit: record?.lastVisit,
			};
			writeRecord(reportId, next);
			setRecord(next);
		},
		[reportId, record],
	);

	// Stamped on unmount only: re-rendering here would clear the "New" badges
	// while the traveler is still reading the batch.
	const markVisited = useCallback(() => {
		if (!effectiveRecord) {
			return;
		}
		writeRecord(reportId, { ...effectiveRecord, lastVisit: moment().format() });
	}, [reportId, effectiveRecord]);

	return {
		state,
		record: effectiveRecord,
		monitoringStart,
		monitoringEnd,
		lastSweepAt,
		nextSweepInHours,
		latestBatchCutoff,
		start,
		markVisited,
	};
}
